import { useState, useEffect, useCallback, useRef } from 'react';
import { supabase } from '../../lib/supabase-client';
import { batchSignUrls } from '../../lib/gcs-client';
import { useAuth } from '../../lib/AuthContext';
import { Property, GalleryImage } from '../../types/property';
import { toast } from 'sonner';

type PhotoCategory = 'enhanced' | 'staged' | 'renovated';

export function useProperties() {
    const { user } = useAuth();
    const [properties, setProperties] = useState<Property[]>([]);
    const [imagesLoading, setImagesLoading] = useState(true);
    const isInitialized = useRef(false);

    // Fetch properties and images
    const fetchProperties = useCallback(async () => {
        if (!user) return;

        try {
            // Only show full loader on initial fetch
            if (!isInitialized.current) {
                setImagesLoading(true);
            }

            // 1. Fetch properties
            const { data: propertiesData, error: propsError } = await supabase
                .from('properties')
                .select('*')
                .eq('user_id', user.id)
                .eq('is_deleted', false)
                .order('created_at', { ascending: false });

            if (propsError) throw propsError;

            if (!propertiesData || propertiesData.length === 0) {
                setProperties([]);
                isInitialized.current = true;
                return;
            }

            const propertyIds = propertiesData.map(p => p.id);

            // 2. Fetch related data in parallel
            const [imagesResult, generationsResult] = await Promise.all([
                supabase
                    .from('images')
                    .select('*')
                    .in('property_id', propertyIds)
                    .order('created_at', { ascending: false }),
                supabase
                    .from('generations')
                    .select('*')
                    .in('property_id', propertyIds)
                    .order('created_at', { ascending: false })
            ]);

            const imagesData = imagesResult.data;
            const generationsData = generationsResult.data;

            const generationIds = generationsData?.map(g => g.id) || [];

            // 3. Fetch outputs (depend on generations)
            const { data: genOutputsData, error: genOutputsError } = generationIds.length > 0
                ? await supabase
                    .from('generation_outputs')
                    .select('*')
                    .in('generation_id', generationIds)
                : { data: [] as any[], error: null };

            if (genOutputsError) {
                console.error('[useProperties] generation_outputs query failed:', genOutputsError);
            }

            // 4. Batch sign URLs
            const pathsToSign: { bucket: string; path: string }[] = [];
            imagesData?.forEach(img => {
                if (img.gcs_path && !img.gcs_path.startsWith('http')) {
                    const bucket = img.image_type === 'original' ? 'wysko-originals' : 'wysko-generated';
                    pathsToSign.push({ bucket, path: img.gcs_path });
                }
            });

            const signedUrlsMap = await batchSignUrls(pathsToSign);

            const getImageUrl = (path: string) => {
                if (!path) return '';
                if (path.startsWith('http')) return path;
                return signedUrlsMap[path] || '';
            };

            // 4. Map to Property objects
            // Maps database generation_mode to display category
            const categoryMap: Record<string, PhotoCategory> = {
                enhance: 'enhanced',
                staging: 'staged',
                renovation: 'renovated'
            };

            const mappedProperties: Property[] = propertiesData.map(prop => {
                const propImages = imagesData?.filter(img => img.property_id === prop.id) || [];
                const originals = propImages.filter(img => img.image_type === 'original');

                const galleryImages: GalleryImage[] = originals.map(originalImg => {
                    const relatedGenerations = generationsData?.filter(gen => gen.source_image_id === originalImg.id) || [];
                    const relatedGenIds = relatedGenerations.map(g => g.id);

                    const outputs = genOutputsData?.filter(out => relatedGenIds.includes(out.generation_id)) || [];
                    const outputImageIds = outputs.map(out => out.output_image_id);

                    // Build provider lookup for source field
                    const providerMap = new Map<string, string>();
                    outputs.forEach(out => {
                        if (out.output_image_id && out.ai_provider) {
                            providerMap.set(out.output_image_id, out.ai_provider);
                        }
                    });

                    const variantImages = propImages.filter(img => outputImageIds.includes(img.id));

                    const variants = variantImages.map((vImg, idx) => ({
                        id: vImg.id,
                        url: getImageUrl(vImg.gcs_path),
                        label: `Wariant ${String.fromCharCode(65 + idx)}`,
                        source: providerMap.get(vImg.id) || undefined,
                    }));

                    const displayUrl = variants.length > 0 ? variants[0].url : getImageUrl(originalImg.gcs_path);

                    const lastGenMode = relatedGenerations[0]?.generation_mode || 'enhance';

                    // Detect failed generation: latest generation is not 'completed' and no successful variants
                    // Covers: 'failed' (explicit), 'processing' (function killed before completing)
                    const hasFailedGeneration = relatedGenerations.length > 0
                        && relatedGenerations[0]?.status !== 'completed'
                        && variants.length === 0;

                    return {
                        id: originalImg.id, // Original ID implies the group ID
                        url: displayUrl,
                        category: categoryMap[lastGenMode] || 'enhanced',
                        aspectRatio: 'landscape' as const,
                        variantCount: variants.length,
                        roomName: originalImg.room_name || 'Pomieszczenie',
                        originalUrl: getImageUrl(originalImg.gcs_path),
                        deleted: originalImg.is_deleted || false,
                        favorite: originalImg.is_favorite || false,
                        variants: variants,
                        generationError: hasFailedGeneration,
                    };
                });

                return {
                    id: prop.id,
                    name: prop.name,
                    address: prop.address || prop.name,
                    photoCount: galleryImages.length,
                    date: new Date(prop.created_at || Date.now()).toLocaleDateString('pl-PL').replace(/\//g, '.'),
                    imageUrl: galleryImages[0]?.url || "data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='400' height='300' fill='%23e2e8f0'%3E%3Crect width='400' height='300'/%3E%3Ctext x='200' y='150' text-anchor='middle' fill='%2394a3b8' font-family='sans-serif' font-size='16'%3EBrak zdj%C4%99%C4%87%3C/text%3E%3C/svg%3E",
                    galleryImages: galleryImages,
                    deleted: prop.is_deleted || false,
                };
            });

            // Merge with existing state to preserve frontend-only flags (e.g. generationError from timeout)
            setProperties(prevProperties => {
                if (prevProperties.length === 0) return mappedProperties;

                return mappedProperties.map(prop => {
                    const prevProp = prevProperties.find(p => p.id === prop.id);
                    if (!prevProp) return prop;

                    return {
                        ...prop,
                        galleryImages: prop.galleryImages.map(img => {
                            const prevImg = prevProp.galleryImages?.find(g => g.id === img.id);
                            // Preserve frontend-set generationError if DB has no generation record yet
                            if (prevImg?.generationError && !img.generationError && img.variants.length === 0) {
                                return { ...img, generationError: true };
                            }
                            return img;
                        }),
                    };
                });
            });
            isInitialized.current = true;
        } catch (error) {
            console.error('Error loading properties:', error);
            toast.error('Błąd podczas ładowania nieruchomości');
        } finally {
            setImagesLoading(false);
        }
    }, [user]);

    // Initial fetch
    useEffect(() => {
        fetchProperties();
    }, [fetchProperties]);

    const createProperty = async (name: string): Promise<string | null> => {
        if (!user) return null;

        const { data, error } = await supabase
            .from('properties')
            .insert({
                user_id: user.id,
                name: name,
                address: name,
            })
            .select()
            .single();

        if (error) {
            console.error('Error creating property:', error);
            toast.error('Nie udało się utworzyć nieruchomości');
            return null;
        }

        if (data) {
            // Optimistic update locally to avoid waiting for fetchProperties
            const newProp: Property = {
                id: data.id,
                address: data.address || data.name,
                photoCount: 0,
                date: new Date(data.created_at || Date.now()).toLocaleDateString('pl-PL').replace(/\//g, '.'),
                imageUrl: "data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='400' height='300' fill='%23e2e8f0'%3E%3Crect width='400' height='300'/%3E%3Ctext x='200' y='150' text-anchor='middle' fill='%2394a3b8' font-family='sans-serif' font-size='16'%3EBrak zdj%C4%99%C4%87%3C/text%3E%3C/svg%3E",
                galleryImages: [],
                deleted: false
            };

            setProperties(prev => [newProp, ...prev]);

            // Background fetch to sync
            fetchProperties();
            toast.success('Nieruchomość została utworzona');
            return data.id;
        }
        return null;
    };

    const renameProperty = async (propertyId: string, newName: string) => {
        const { error } = await supabase
            .from('properties')
            .update({ name: newName, address: newName })
            .eq('id', propertyId);

        if (error) {
            console.error('Error renaming property:', error);
            toast.error('Nie udało się zmienić nazwy');
            return;
        }

        setProperties(prev => prev.map(p =>
            p.id === propertyId ? { ...p, address: newName } : p
        ));
    };

    const deleteProperty = async (propertyId: string) => {
        const { error } = await supabase
            .from('properties')
            .update({ is_deleted: true, deleted_at: new Date().toISOString() })
            .eq('id', propertyId);

        if (error) {
            console.error('Error deleting property:', error);
            toast.error('Nie udało się usunąć nieruchomości');
            return;
        }

        setProperties(prev => prev.filter(p => p.id !== propertyId));
        toast.success('Nieruchomość została usunięta');
    };

    return {
        properties,
        setProperties,
        imagesLoading,
        fetchProperties,
        createProperty,
        renameProperty,
        deleteProperty
    };
}
